// src/portal-app/team/PayRunsScreen.js
//
// Plan 4 Task 10 — Pay Runs screen, migrated to the P4 pattern (spec §5).
// See operations/TransactionsScreen.js:32-55 for the pattern (and its file
// header) this follows; team/EmployeesScreen.js (Plan 4 Task 9) is the
// closest reviewed model in this same directory. This header only calls out
// decisions specific to Pay Runs.
//
// Dropped zone — the "To Run" urgency strip: the pre-migration screen had
// TWO row types (colored UrgencyCard strip from `to_run[]`, plus GlassCard
// RunCard list from `runs[]`) plus two FilterRows before a single pay run
// appeared. The P4 recipe is one hero + one list; there is no slot for a
// second, differently-styled row type. Dropping it is NOT information loss:
// every pay run in `to_run` is already a member of `runs` (payrun_service.
// list_pay_runs builds `to_run` by filtering the SAME query that becomes
// `runs`, never removing matches from `runs`), so every run this screen
// used to highlight still renders here, in the one list, with its real
// status pill. The urgency triage itself (red/amber/green, "which run needs
// me first") already has a dedicated home one level up: TeamTab.js
// (tabs/TeamTab.js:142-160, 251-257) renders every due pay run through
// AttentionList with the same red/amber/green tone, a "Pay run due {date}"
// line and its owed amount, and tapping it pushes here — duplicating that
// strip on this screen would be the exact redundant-padding the recipe
// removes (spec §6). `unpaid_count` and `urgency` (to_run-only fields) are
// therefore not read by this screen at all.
//
// Row source — `runs`, not `to_run`. `runs` is the complete, currently
// filtered set (every status, including paid/closed) and is exactly what
// renders below the hero, so hero and rows share one denominator (recipe
// rule 3). `to_run` is a strict subset used only for the dropped zone above.
//
// Row fields (brief: "period, employee count, amount, StatusBadge for run
// status") map onto CondensedRow as:
//   name      — period (fmtPeriod(period_start, period_end)) — a pay run's
//               period is its identity, same role `time · staff` plays for
//               a Transaction row.
//   value     — total_net. Following the plan-wide precedent (Transactions'
//               final_amount, Products' selling_price, Expenses'
//               total_cost, Inventory's qty unit): the row's headline
//               number is the record's OWN total, not a derived remaining
//               balance. A pay run's own total is its net pay (what the
//               run is worth), matching PayRunDetailScreen's SummaryStrip
//               where "Net" — not "Owed" — is the run's primary figure.
//   secondary — "{cadence} · {employeeCount} Employees", composed the same
//               way Transactions composes products_summary + dining_option
//               and Employees composes role + department + Cashier: two
//               real fields folded into the one text slot rather than
//               dropped. Employee count reads `line_items.length` —
//               PayRunResponse (backend/app/models/pay_models.py:456-471)
//               carries the full line_items array, so no extra fetch is
//               needed to count heads.
//   pill      — {label: translated status, tone} — status IS the row's
//               StatusBadge per the brief, same "the pill IS that slot"
//               rule EmployeesScreen documents (its own file header) for
//               CondensedRow's single-pill contract.
//   icon      — IconCash (shell/kit), tone-coded by the SAME STATUS_TONE
//               map as the pill. No categorical field on a pay run plays
//               the role payment-method plays for Transactions (something
//               to vary the glyph by), so — like ProductsScreen's IconTag
//               and InventoryScreen's IconPackage — one glyph is used for
//               every row; only its tone changes.
//
// Dropped/changed fields (task Lesson 5):
//   - pay_date is dropped from the row (available one tap away on
//     PayRunDetailScreen, which already headlines it in its chrome
//     subtitle). Two-line CondensedRow has no fifth slot; period, count,
//     net and status already fill both lines.
//   - total_gross / total_deductions are dropped from the row (available
//     on PayRunDetailScreen's SummaryStrip) — the brief's field list is
//     explicit and doesn't include them.
//   - `total_owed` on a `runs`-array item does NOT exist on the wire:
//     PayRunResponse (pay_models.py:456-471) has total_gross/
//     total_deductions/total_net/total_paid only; `total_owed` is a key
//     the backend stamps ONLY on `to_run` dict entries (payrun_service.py
//     :509-528), a Python dict PayRunListResponse never validates against
//     PayRunResponse. The pre-migration RunCard read `run.total_owed` on
//     `runs`-array items anyway, which was always `undefined` -> 0 for
//     every run in the "All Runs" section — a real pre-existing display
//     bug (an open "ready" run showed "Owed £0.00" in that list even
//     while genuinely owing money; only its OTHER copy in the to_run strip
//     showed the true figure). This migration computes owed as
//     max(0, total_net - total_paid) — the same formula
//     PayRunDetailScreen already uses (PayRunDetailScreen.js:575) — so the
//     hero and ShareBar total correctly for every run, not just the
//     draft/ready/partially_paid subset the backend happened to stamp.
//
// Negative values (task ambiguity note) — checked against the schema
// (pay_models.py): total_net has no CHECK constraint and CAN go negative
// in principle (enough employees with deductions exceeding gross for the
// period). But the two figures this screen feeds into ShareBar never can:
//   - "Paid" is total_paid, a sum of recorded PayPayment.amount rows; the
//     payment sheet on PayRunDetailScreen rejects amount <= 0
//     (PaymentSheet's submit guard, PayRunDetailScreen.js:253-254), so no
//     payment is ever negative.
//   - "Owed" is Math.max(0, total_net - total_paid) — floored at zero by
//     construction, exactly like PayRunDetailScreen's own totalOwed.
// So a pay run with negative total_net still contributes 0 to "Owed", never
// a negative segment. ShareBar's Task 3 contract (positive-only
// proportions; negatives excluded from the bar but kept in the legend) is
// therefore never actually exercised BY THIS SCREEN's hero — both segments
// it can ever produce are >= 0. A negative total_net is still visible,
// unstyled, in the affected row's own `value` text (e.g. "-£40.00"), same
// as PayRunDetailScreen already renders it un-specially at the run level
// (only per-employee net gets `.prdt-li-metric-neg` treatment there).
//
// Permission gating — F13 Task 1. This screen previously gated nothing: the
// hub CARD (HUBS.team.cards payruns, permission 'portal:payroll',
// nav/tabs.js) decided only whether Pay Runs was reachable, on can_VIEW.
// Anyone who could open the screen could also create a draft run. The
// create affordance now requires can_EDIT on 'portal:payroll' via
// useAuth().canEdit, matching the Manager default role (payroll
// can_view=true / can_edit=false — view the payroll, never run it).
//
// This is UX, not security. The authority lives in the backend: every
// /payrun mutation carries `require_payroll_edit` and every read carries
// `require_payroll_view` (backend/app/routers/payrun.py), enforced against
// the same role_permissions grid the login response hands AuthContext. If
// this file were deleted the endpoints would still refuse.
//
// Filters — cadence + status, both server-side (payrunService.list({
// cadence, status }) triggers a re-fetch), collapsed into ONE FilterSheet
// exactly as the brief specifies ("Both FilterRows into the sheet"). Unlike
// EmployeesScreen (client-side filtering over one fetched array), status
// here is NOT client-derived: `status` state is set directly from what the
// server returned for the CURRENT filter params, so a filter producing zero
// server-side matches is a genuine 'empty', not a cosmetic 0-length array —
// see the task report's mutation-evidence section.
//
// No SearchInput — the pre-migration screen never had one, and the brief's
// row-field list doesn't add one. Pay run counts are cadence-bounded (a
// handful of weekly/monthly runs per year) — nowhere near the "~50 items"
// threshold the recipe uses to justify search.
//
// This screen renders CONTENT ONLY — NavStackHost owns the header.
import React, {
    useState, useEffect, useCallback, useMemo, useRef,
} from 'react';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { payrunService, payrollConfigService } from '../../services/api';
import { toLocalISO } from '../nav/dateRange';
import { SurfaceStates, fmtMoney } from '../insights/kit';
import {
    CondensedRow, ShareBar, FilterButton, FilterSheet, HeroStat,
    PullToRefresh, ensureKitCss, IconCash,
} from '../shell/kit';
import { GlassCard } from '../shell/LiquidGlass';
import { useNav, useNavChrome } from '../nav/NavStack';

/* ------------------------------------------------------------------ */
/* Stylesheet                                                          */
/* ------------------------------------------------------------------ */
const CSS = `
/* ---- layout ---- */
.pr-wrap{width:100%;max-width:680px;margin:0 auto;box-sizing:border-box;
  padding:8px 0 120px;}

/* ---- hero ---- */
.pr-hero{margin:0 16px 16px;padding:18px 20px 16px;}
.pr-hero-share{margin-top:14px;}

/* ---- control row — FilterButton only (no RangeBar equivalent) ---- */
.pr-control-row{display:flex;justify-content:flex-end;padding:0 16px;
  margin-bottom:10px;}

/* ---- view-only notice (no can_edit on portal:payroll) ---- */
.pr-readonly{display:flex;align-items:center;gap:8px;margin:0 16px 12px;
  padding:10px 12px;border-radius:12px;
  border:1px solid var(--c-border);background:var(--c-surface);
  font-size:12px;font-weight:600;color:var(--c-textLight);}
.pr-readonly svg{flex:none;}

/* ---- active-filter summary ---- */
.pr-filtersum{display:flex;align-items:center;gap:12px;
  margin:4px 16px 12px;}
.pr-filtersum-text{flex:1 1 auto;min-width:0;font-size:12px;font-weight:600;
  color:var(--c-textLight);overflow:hidden;text-overflow:ellipsis;
  white-space:nowrap;}
.pr-filtersum-clear{flex:none;min-height:44px;padding-inline:6px;border:none;
  background:transparent;font-family:inherit;font-size:12px;font-weight:700;
  color:var(--c-green);cursor:pointer;}

/* ---- count line + list ---- */
.pr-count-line{font-size:11px;font-weight:700;text-transform:uppercase;
  letter-spacing:1.2px;color:var(--c-textLight);margin:2px 16px 10px;}
.pr-list{display:flex;flex-direction:column;gap:10px;padding:0 16px;}

/* ---- create sheet backdrop ---- */
.pr-sheet-backdrop{position:fixed;inset:0;z-index:1000;
  background:color-mix(in srgb,var(--c-ink) 45%,transparent);
  display:flex;align-items:flex-end;}
.pr-sheet{width:100%;max-width:560px;margin:0 auto;
  padding:24px 20px calc(24px + var(--safe-bottom,0px));
  box-sizing:border-box;border-radius:24px 24px 0 0;}
.pr-sheet-drag{width:36px;height:4px;border-radius:2px;margin:0 auto 20px;
  background:color-mix(in srgb,var(--c-ink) 20%,transparent);}
.pr-sheet-title{font-size:18px;font-weight:800;letter-spacing:-.3px;
  color:var(--c-ink);margin-bottom:4px;}
.pr-sheet-sub{font-size:13px;font-weight:500;color:var(--c-textLight);
  margin-bottom:20px;}
.pr-cadence-options{display:flex;gap:8px;margin-bottom:20px;flex-wrap:wrap;}
.pr-cadence-opt{flex:1;min-width:80px;min-height:48px;border-radius:14px;
  border:1.5px solid color-mix(in srgb,var(--c-ink) 10%,transparent);
  background:color-mix(in srgb,var(--c-surface) 50%,transparent);
  font-family:inherit;font-size:13px;font-weight:700;color:var(--c-ink);
  cursor:pointer;display:flex;align-items:center;justify-content:center;
  transition:background .12s ease,border-color .12s ease;}
.pr-cadence-opt-active{background:var(--c-green);border-color:var(--c-green);
  color:var(--c-white);}
.pr-period-preview{border-radius:14px;padding:14px 16px;margin-bottom:20px;
  display:flex;gap:20px;flex-wrap:wrap;}
.pr-period-preview-item{display:flex;flex-direction:column;gap:3px;}
.pr-period-preview-label{font-size:10px;font-weight:700;letter-spacing:.6px;
  text-transform:uppercase;color:var(--c-mutedLight);}
.pr-period-preview-value{font-size:14px;font-weight:700;color:var(--c-ink);}
.pr-sheet-actions{display:flex;gap:10px;}
.pr-sheet-cancel{flex:1;min-height:48px;border-radius:16px;
  border:1.5px solid color-mix(in srgb,var(--c-ink) 12%,transparent);
  background:transparent;font-family:inherit;font-size:15px;font-weight:700;
  color:var(--c-ink);cursor:pointer;
  transition:background .12s ease;}
.pr-sheet-cancel:active{background:color-mix(in srgb,var(--c-ink) 5%,transparent);}
.pr-sheet-submit{flex:1;min-height:48px;border-radius:16px;border:none;
  background:var(--c-green);font-family:inherit;font-size:15px;font-weight:700;
  color:var(--c-white);cursor:pointer;
  display:inline-flex;align-items:center;justify-content:center;gap:8px;
  transition:opacity .15s ease;}
.pr-sheet-submit:disabled{opacity:.5;cursor:not-allowed;}
.pr-spinner{width:16px;height:16px;border-radius:50%;flex:none;
  border:2.5px solid color-mix(in srgb,var(--c-white) 30%,transparent);
  border-top-color:var(--c-white);
  animation:prSpin .75s linear infinite;}
@keyframes prSpin{to{transform:rotate(360deg);}}
@media(prefers-reduced-motion:reduce){.pr-spinner{animation-duration:1.5s;}}

/* ---- create trailing button ---- */
.pr-create-btn{min-height:36px;padding:0 14px;border-radius:12px;border:none;
  background:var(--c-green);font-family:inherit;font-size:13px;font-weight:700;
  color:var(--c-white);cursor:pointer;display:inline-flex;align-items:center;
  gap:6px;transition:opacity .15s ease;}
.pr-create-btn:active{opacity:.85;}
`;

let cssInjected = false;
function ensureCss() {
    if (cssInjected || typeof document === 'undefined') return;
    cssInjected = true;
    const el = document.createElement('style');
    el.id = 'pr-css';
    el.textContent = CSS;
    document.head.appendChild(el);
}

/* ------------------------------------------------------------------ */
/* Helpers                                                             */
/* ------------------------------------------------------------------ */
const addDays = (d, n) => { const r = new Date(d); r.setDate(r.getDate() + n); return r; };

function fmtDate(iso) {
    if (!iso) return '—';
    // Parse as local to avoid UTC offset shifting the date
    const [y, m, d] = iso.split('-').map(Number);
    const dt = new Date(y, m - 1, d);
    if (isNaN(dt.getTime())) return '—';
    return dt.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function fmtPeriod(start, end) {
    return `${fmtDate(start)} – ${fmtDate(end)}`;
}

function calcPeriod(cadence, cfg) {
    const now = new Date();
    if (cadence === 'monthly') {
        const cutoff = cfg?.monthly_cutoff_day || 31;
        const offset = cfg?.monthly_pay_offset_days || 0;
        const y = now.getFullYear(), m = now.getMonth();
        const lastDay = new Date(y, m + 1, 0).getDate();
        const endDay = Math.min(cutoff, lastDay);
        const end = new Date(y, m, endDay);
        const start = new Date(end);
        start.setMonth(start.getMonth() - 1);
        start.setDate(start.getDate() + 1);
        const payDay = Math.min(endDay + offset, lastDay);
        return {
            period_start: toLocalISO(start),
            period_end: toLocalISO(end),
            pay_date: toLocalISO(new Date(y, m, payDay)),
        };
    }
    if (cadence === 'weekly') {
        const cutoffDow = cfg?.weekly_cutoff_day_of_week ?? 7;
        const offset = cfg?.weekly_pay_offset_days ?? 1;
        const currentDow = now.getDay() || 7; // 1-7 (Mon=1, Sun=7)
        const diff = cutoffDow - currentDow;
        const end = addDays(now, diff <= 0 ? diff : diff - 7);
        const start = addDays(end, -6);
        return {
            period_start: toLocalISO(start),
            period_end: toLocalISO(end),
            pay_date: toLocalISO(addDays(end, offset)),
        };
    }
    // daily fallback
    return {
        period_start: toLocalISO(now),
        period_end: toLocalISO(now),
        pay_date: toLocalISO(now),
    };
}

/* ------------------------------------------------------------------ */
/* Status tone — shared by the row's pill AND its icon. 'muted' (not   */
/* the pre-migration 'gray') matches the kit's tone vocabulary          */
/* (shell/kit/tones.js has no 'gray' entry — toneVar() would silently   */
/* fall back to 'blue' for an unrecognised name).                       */
/* ------------------------------------------------------------------ */
const STATUS_TONE = {
    draft:          'muted',
    ready:          'amber',
    partially_paid: 'amber',
    paid:           'green',
    closed:         'muted',
};

/* ------------------------------------------------------------------ */
/* Create Draft Sheet — unchanged behaviour/payload from pre-migration  */
/* (same cadence picker, same calcPeriod, same createDraft() call).     */
/* ------------------------------------------------------------------ */
const CADENCES = ['monthly', 'weekly', 'daily'];

function CreateSheet({ cfg, onSubmit, onClose, saving, t }) {
    const [cadence, setCadence] = useState('monthly');
    const period = calcPeriod(cadence, cfg);

    const handleCadence = (c) => setCadence(c);

    return (
        <div className="pr-sheet-backdrop" onClick={(e) => e.target === e.currentTarget && onClose()}>
            <GlassCard veil className="pr-sheet">
                <div className="pr-sheet-drag" />
                <div className="pr-sheet-title">{t('start_new_run', {}, 'New Pay Run')}</div>
                <div className="pr-sheet-sub">{t('new_run_subtitle', {}, 'Select cadence — period is auto-calculated')}</div>

                <div className="pr-cadence-options">
                    {CADENCES.map((c) => (
                        <button
                            key={c}
                            className={`pr-cadence-opt${cadence === c ? ' pr-cadence-opt-active' : ''}`}
                            onClick={() => handleCadence(c)}
                            data-testid={`cadence-option-${c}`}
                        >
                            {t(c, {}, c)}
                        </button>
                    ))}
                </div>

                {period.period_start && (
                    <div className="pr-period-preview">
                        <div className="pr-period-preview-item">
                            <span className="pr-period-preview-label">{t('period', {}, 'Period')}</span>
                            <span className="pr-period-preview-value">
                                {fmtPeriod(period.period_start, period.period_end)}
                            </span>
                        </div>
                        <div className="pr-period-preview-item">
                            <span className="pr-period-preview-label">{t('pay_date', {}, 'Pay Date')}</span>
                            <span className="pr-period-preview-value">{fmtDate(period.pay_date)}</span>
                        </div>
                    </div>
                )}

                <div className="pr-sheet-actions" data-testid="create-sheet">
                    <button className="pr-sheet-cancel" onClick={onClose}>
                        {t('cancel', {}, 'Cancel')}
                    </button>
                    <button
                        className="pr-sheet-submit"
                        disabled={saving}
                        onClick={() => onSubmit({ cadence, ...period })}
                        data-testid="create-submit-btn"
                    >
                        {saving && <span className="pr-spinner" />}
                        {saving ? t('saving', {}, 'Saving…') : t('create', {}, 'Create')}
                    </button>
                </div>
            </GlassCard>
        </div>
    );
}

/* ------------------------------------------------------------------ */
/* Trailing chrome button                                              */
/* ------------------------------------------------------------------ */
function CreateButton({ t, onClick }) {
    return (
        <button className="pr-create-btn" onClick={onClick} data-testid="create-run-btn">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="12" y1="5" x2="12" y2="19"/>
                <line x1="5" y1="12" x2="19" y2="12"/>
            </svg>
            {t('new_run', {}, 'New Run')}
        </button>
    );
}

/* ------------------------------------------------------------------ */
/* View-only notice                                                    */
/* ------------------------------------------------------------------ */
// A screen whose only action has silently vanished reads as broken. The
// icon is decorative (aria-hidden); the adjacent text carries the meaning,
// so the notice never depends on colour or glyph alone.
function ViewOnlyNotice({ t, className }) {
    return (
        <div className={className} data-testid="payroll-view-only">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true"
                stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="11" width="18" height="11" rx="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
            <span>{t('payroll_view_only', {}, 'View-only access — you cannot change pay runs.')}</span>
        </div>
    );
}

/* ------------------------------------------------------------------ */
/* Filter data                                                         */
/* ------------------------------------------------------------------ */
const CADENCE_OPTIONS = [
    { id: 'daily',   fallback: 'daily' },
    { id: 'weekly',  fallback: 'weekly' },
    { id: 'monthly', fallback: 'monthly' },
];

const STATUS_OPTIONS = [
    { id: 'draft',   fallback: 'draft' },
    { id: 'ready',   fallback: 'ready' },
    { id: 'paid',    fallback: 'paid' },
    { id: 'closed',  fallback: 'closed' },
];

/* ------------------------------------------------------------------ */
/* Pay run row — CondensedRow. See file header for the field mapping.   */
/* ------------------------------------------------------------------ */
function PayRunRow({ run, currency, onPress, t }) {
    const employeeCount = Array.isArray(run.line_items) ? run.line_items.length : 0;
    const secondary = [
        t(run.cadence, {}, run.cadence),
        `${employeeCount} ${t('employees', {}, 'Employees')}`,
    ].filter(Boolean).join(' · ');
    const tone = STATUS_TONE[run.status] || 'muted';

    return (
        <CondensedRow
            icon={<IconCash />}
            tone={tone}
            name={fmtPeriod(run.period_start, run.period_end)}
            value={fmtMoney(run.total_net, currency)}
            secondary={secondary}
            pill={{ label: t(`status_${run.status}`, {}, run.status), tone }}
            onPress={onPress}
            testId={run.pay_run_id}
        />
    );
}

/* ------------------------------------------------------------------ */
/* Main screen                                                         */
/* ------------------------------------------------------------------ */
export default function PayRunsScreen() {
    ensureCss();
    ensureKitCss();
    const { getCurrency, canEdit } = useAuth();
    const { t, isRTL } = useLanguage();
    const { push } = useNav();
    const currency = getCurrency();
    // can_EDIT, not can_view — reaching this screen only proves view rights.
    const canEditPayroll = canEdit('portal:payroll');

    const [runs, setRuns] = useState([]);
    const [status, setStatus] = useState('loading'); // loading | error | empty | ready
    const [cadenceFilter, setCadenceFilter] = useState(null);
    const [statusFilter, setStatusFilter] = useState(null);
    const [filterSheetOpen, setFilterSheetOpen] = useState(false);

    const [showCreate, setShowCreate] = useState(false);
    const [saving, setSaving] = useState(false);
    const [cfg, setCfg] = useState(null);

    const fetchRef = useRef(0);

    // Load payroll config once on mount for period calculation
    useEffect(() => {
        payrollConfigService.get().then(setCfg).catch(() => {});
    }, []);

    const load = useCallback(async () => {
        const id = ++fetchRef.current;
        setStatus('loading');
        try {
            const params = {};
            if (cadenceFilter) params.cadence = cadenceFilter;
            if (statusFilter) params.status = statusFilter;
            const data = await payrunService.list(params);
            if (id !== fetchRef.current) return; // stale
            const r = Array.isArray(data?.runs) ? data.runs : [];
            setRuns(r);
            setStatus(r.length === 0 ? 'empty' : 'ready');
        } catch {
            if (id !== fetchRef.current) return; // stale
            setStatus('error');
        }
    }, [cadenceFilter, statusFilter]);

    useEffect(() => { load(); }, [load]);

    // Trailing chrome — create button, edit rights only.
    useNavChrome({
        trailing: canEditPayroll
            ? () => (
                <CreateButton t={t} onClick={() => setShowCreate(true)} />
            )
            : undefined,
    }, [canEditPayroll]);

    const handlePush = (run) => {
        push('payrun-detail', {
            payRunId: run.pay_run_id,
            period: fmtPeriod(run.period_start, run.period_end),
        });
    };

    const handleCreate = async (payload) => {
        setSaving(true);
        try {
            const created = await payrunService.createDraft(payload);
            setShowCreate(false);
            if (created?.pay_run_id) {
                push('payrun-detail', {
                    payRunId: created.pay_run_id,
                    period: fmtPeriod(created.period_start, created.period_end),
                });
            } else {
                load();
            }
        } catch {
            // surface the error via a re-fetch to show updated state
            load();
        } finally {
            setSaving(false);
        }
    };

    // ── Derived: hero — paid vs outstanding across the CURRENT filtered  ──
    //    set (`runs`), the exact array rendered below it (recipe rule 3). ──
    const totalPaid = useMemo(
        () => runs.reduce((sum, r) => sum + Number(r.total_paid || 0), 0),
        [runs],
    );
    const totalOwed = useMemo(
        () => runs.reduce(
            (sum, r) => sum + Math.max(0, Number(r.total_net || 0) - Number(r.total_paid || 0)),
            0,
        ),
        [runs],
    );
    const showShareBar = (totalPaid + totalOwed) > 0;
    const shareSegments = [
        { id: 'paid', label: t('paid', {}, 'Paid'), value: totalPaid, display: fmtMoney(totalPaid, currency), tone: 'green' },
        { id: 'owed', label: t('owed', {}, 'Owed'), value: totalOwed, display: fmtMoney(totalOwed, currency), tone: 'amber' },
    ];
    const shareAriaLabel = t(
        'paid_owed_split_aria',
        { paid: fmtMoney(totalPaid, currency), owed: fmtMoney(totalOwed, currency) },
        'Paid {paid}, Owed {owed}',
    );

    // ── Derived: FilterSheet groups + active-filter summary ───────────
    const filterGroups = useMemo(() => [
        {
            id: 'cadence',
            label: t('cadence', {}, 'Cadence'),
            options: CADENCE_OPTIONS.map((f) => ({ id: f.id, label: t(f.id, {}, f.fallback) })),
        },
        {
            id: 'status',
            label: t('status', {}, 'Status'),
            options: STATUS_OPTIONS.map((f) => ({ id: f.id, label: t(`status_${f.id}`, {}, f.fallback) })),
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
    ], [t]);

    const filterValue = { cadence: cadenceFilter, status: statusFilter };
    const activeFilterCount = [cadenceFilter, statusFilter].filter(Boolean).length;
    const activeFilterText = [
        cadenceFilter ? t(cadenceFilter, {}, cadenceFilter) : null,
        statusFilter ? t(`status_${statusFilter}`, {}, statusFilter) : null,
    ].filter(Boolean).join(' · ');

    const handleFilterChange = (groupId, optionId) => {
        if (groupId === 'cadence') setCadenceFilter(optionId);
        else if (groupId === 'status') setStatusFilter(optionId);
    };

    const clearAllFilters = () => {
        setCadenceFilter(null);
        setStatusFilter(null);
    };

    const ptrDisabled = showCreate || filterSheetOpen;

    return (
        <div className="pr-wrap" dir={isRTL ? 'rtl' : 'ltr'}>
            {/* Hero card — paid vs owed across the filtered runs. OUTSIDE
                SurfaceStates so it never vanishes. No ShareBar when there is
                nothing paid or owed (both segments would be 0). */}
            <GlassCard veil className="pr-hero" data-testid="payruns-hero">
                <HeroStat
                    label={t('owed', {}, 'Owed')}
                    value={fmtMoney(totalOwed, currency)}
                />
                {showShareBar && (
                    <div className="pr-hero-share">
                        <ShareBar segments={shareSegments} ariaLabel={shareAriaLabel} />
                    </div>
                )}
            </GlassCard>

            {/* View-only notice — replaces the create affordance rather than
                leaving its absence unexplained. */}
            {!canEditPayroll && <ViewOnlyNotice t={t} className="pr-readonly" />}

            {/* Control row — FilterButton only; no RangeBar equivalent. */}
            <div className="pr-control-row">
                <FilterButton
                    activeCount={activeFilterCount}
                    onPress={() => setFilterSheetOpen(true)}
                />
            </div>

            {/* Active-filter summary — OUTSIDE SurfaceStates so a filtered-to-
                empty result still shows the means to clear the filter. */}
            {activeFilterCount > 0 && (
                <div className="pr-filtersum">
                    <span className="pr-filtersum-text">{activeFilterText}</span>
                    <button
                        type="button"
                        className="pr-filtersum-clear"
                        data-testid="clear-filters"
                        onClick={clearAllFilters}
                    >
                        {t('clear', {}, 'Clear')}
                    </button>
                </div>
            )}

            <PullToRefresh onRefresh={load} disabled={ptrDisabled}>
                <SurfaceStates
                    status={status}
                    onRetry={load}
                    emptyTitle={
                        activeFilterCount > 0
                            ? t('no_pay_runs_match_filter', {}, 'No pay runs match filters')
                            : t('no_pay_runs', {}, 'No pay runs yet')
                    }
                    emptyMsg={
                        // eslint-disable-next-line no-nested-ternary
                        activeFilterCount > 0
                            ? t('try_adjusting_filter', {}, 'Try adjusting your filter')
                            : canEditPayroll
                                ? t('no_pay_runs_msg', {}, 'Create your first pay run to get started.')
                                : t('no_pay_runs_view_only_msg', {}, 'No pay runs have been created yet.')
                    }
                >
                    {/* Count line */}
                    <div className="pr-count-line" data-testid="pr-count-line">
                        {t('n_pay_runs', { count: runs.length }, '{count} Pay Runs')}
                    </div>

                    {/* Pay run list */}
                    <div className="pr-list">
                        {runs.map((run) => (
                            <PayRunRow
                                key={run.pay_run_id}
                                run={run}
                                currency={currency}
                                onPress={() => handlePush(run)}
                                t={t}
                            />
                        ))}
                    </div>
                </SurfaceStates>
            </PullToRefresh>

            {/* ── Create draft sheet ── */}
            {showCreate && (
                <CreateSheet
                    cfg={cfg}
                    onSubmit={handleCreate}
                    onClose={() => setShowCreate(false)}
                    saving={saving}
                    t={t}
                />
            )}

            {/* ── Filter sheet — cadence / status ── */}
            <FilterSheet
                open={filterSheetOpen}
                onClose={() => setFilterSheetOpen(false)}
                groups={filterGroups}
                value={filterValue}
                onChange={handleFilterChange}
                onClear={clearAllFilters}
            />
        </div>
    );
}
